import { useCallback, useEffect, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import NumberInput from 'fe-design-base/atoms/NumberInput';
import Text from 'fe-design-base/atoms/Text';
import { InputChangeEvent } from 'fe-design-base/baseTypes';
import AlertText from 'fe-design-base/molecules/AlertText';
import RadioButtonField from 'fe-design-base/molecules/RadioButtonField';
import Tooltip from 'fe-design-base/molecules/Tooltip';
import RadioButtonGroup from 'fe-design-base/organisms/RadioButtonGroup';
import { FormikValues, useFormikContext } from 'formik';

import { TabletAndBelow } from 'components/MediaQuery';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';

import FormFieldSpacer from './FormFieldSpacer';

const { cx } = cxHelpers('MiscGarnishmentFormFieldRows');

interface FormValues extends FormikValues {
  amount_type?: 'amount' | 'percent';
  amount?: string;
  maximum_amount?: string;
  annual_limit?: string;
  maximum_percent?: string;
  priority?: string;
}

interface MiscGarnishmentFormFieldRowsProps {
  children: React.ReactNode;
  values: FormValues;
  showFormWithSteps?: boolean;
}

const MiscGarnishmentFormFieldRows = ({
  children,
  values,
  showFormWithSteps,
}: MiscGarnishmentFormFieldRowsProps) => {
  const { setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<FormValues>();

  const [amountType, setAmountType] = useState<'amount' | 'percent'>(
    values.amount_type || 'amount'
  );

  // Watch for changes to amount_type and handle the side effects
  useEffect(() => {
    const newAmountType = values.amount_type as 'amount' | 'percent';
    if (newAmountType && newAmountType !== amountType) {
      setAmountType(newAmountType);

      // When switching to percent, copy the current amount value to maximum_percent
      if (newAmountType === 'percent' && values.amount) {
        setFieldValue('maximum_percent', values.amount, false);
        // Trigger validation after setting the value
        setTimeout(() => {
          setFieldTouched('maximum_percent', true, true);
        }, 0);
      }

      // When switching to amount, copy the current amount value to maximum_amount
      if (newAmountType === 'amount' && values.amount) {
        setFieldValue('maximum_amount', values.amount, false);
        setFieldValue('annual_limit', values.amount, false);
        // Trigger validation after setting the value
        setTimeout(() => {
          setFieldTouched('maximum_amount', true, true);
          setFieldTouched('annual_limit', true, true);
        }, 0);
      }
    }
  }, [
    values.amount_type,
    values.amount,
    amountType,
    setFieldValue,
    setFieldTouched,
  ]);

  const handleAmountChange = useCallback(
    ({ target: { value } }: InputChangeEvent) => {
      if (amountType === 'percent') {
        // Set both fields without validation first
        setFieldValue('amount', value, false);
        setFieldValue('maximum_percent', value, false);

        // Then trigger validation for both fields.
        // This is to avoid validation from being triggered too early.
        setTimeout(() => {
          setFieldTouched('amount', true, true);
          setFieldTouched('maximum_percent', true, true);
        }, 0);
      } else {
        // For fixed amount, also sync with maximum_amount if it exists
        setFieldValue('amount', value, false);
        setFieldValue('maximum_amount', value, false);
        setFieldValue('annual_limit', value, false);

        // setting the fields to touched to trigger validation
        setTimeout(() => {
          setFieldTouched('amount', true, true);
          setFieldTouched('maximum_amount', true, true);
          setFieldTouched('annual_limit', true, true);
        }, 0);
      }
    },
    [setFieldValue, setFieldTouched, amountType]
  );

  const handleFieldChange = useCallback(
    (fieldName: string, validateImmediately = true) =>
      ({ target: { value } }: InputChangeEvent) => {
        setFieldValue(fieldName, value, !validateImmediately);
        if (validateImmediately) {
          setTimeout(() => {
            setFieldTouched(fieldName, true, true);
          }, 0);
        }
      },
    [setFieldValue, setFieldTouched]
  );

  return (
    <TabletAndBelow>
      {(tabletAndBelow: boolean) => (
        <Box className={cx()} pb={20}>
          {/* First row */}
          <Box column={tabletAndBelow} row={!tabletAndBelow} mb={24} mr={24}>
            {/* First column: Contribution Limit */}
            <Box grow basis="40%" ml={8}>
              <Box mb={16}>
                <Text
                  variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                  color="mono700"
                  i18n="team.employee_profile.tax_deductions.employee_contribution_label"
                />
              </Box>

              <RadioButtonGroup name="amount_type">
                <RadioButtonField
                  label={toI18n(
                    'team.employee_profile.tax_deductions.fixed_amount_label'
                  )}
                  value="amount"
                  checked={amountType === 'amount'}
                />
                <RadioButtonField
                  label={
                    <Box row vcenter>
                      <Text>
                        {toI18n(
                          'team.employee_profile.tax_deductions.percent_label'
                        )}
                      </Text>
                      <Box ml={8} borderBox={false}>
                        <Tooltip
                          content={toI18n(
                            'team.employee_profile.tax_deductions.percent_tooltip_content'
                          )}
                          placement="bottom-start"
                        >
                          <Icon
                            size="small"
                            iconName="Info"
                            color="primary500"
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  }
                  value="percent"
                  checked={amountType === 'percent'}
                />

                <Box row>
                  <Box w={185}>
                    {values.amount_type === 'amount' ? (
                      <NumberInput
                        key="amount-input"
                        name="amount"
                        prefix="$"
                        places={2}
                        requireDecimal
                        leftAlign
                        inputMode="decimal"
                        value={values.amount ?? ''}
                        onChange={handleAmountChange}
                        error={!!(errors.amount && touched.amount)}
                      />
                    ) : (
                      <NumberInput
                        key="percent-input"
                        name="amount"
                        suffix="%"
                        places={2}
                        requireDecimal
                        leftAlign
                        inputMode="decimal"
                        value={values.amount ?? ''}
                        onChange={handleAmountChange}
                        error={!!(errors.amount && touched.amount)}
                      />
                    )}
                  </Box>
                  <Box vcenter ml={12}>
                    <Text
                      variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                      color="mono700"
                      i18n="team.employee_profile.tax_deductions.per_pay_period_label"
                    />
                  </Box>
                </Box>
                {errors.amount && touched.amount ? (
                  <Box mt={4}>
                    <AlertText variant="error" message={errors.amount} />
                  </Box>
                ) : (
                  <FormFieldSpacer />
                )}
              </RadioButtonGroup>
            </Box>
            {/* Second column in first row */}
            <Box grow basis="60%" ml={8}>
              <Box mb={24}>
                <Text
                  variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                  color="mono700"
                  data-testid="employeeContributionLimitLabel"
                  i18n="team.employee_profile.tax_deductions.employee_contribution_limit_label"
                />
              </Box>

              <Box mb={24}>
                <Box row vcenter>
                  <Box mr={4}>
                    <Text
                      variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                      color="mono700"
                      i18n="team.employee_profile.tax_deductions.maximum_amount_label"
                    />
                  </Box>
                  <Box mr={12}>
                    <Tooltip
                      content={toI18n(
                        'team.employee_profile.tax_deductions.max_amount_tooltip_content'
                      )}
                      placement="bottom-start"
                    >
                      <Icon size="small" iconName="Info" color="primary500" />
                    </Tooltip>
                  </Box>

                  <Box w={172}>
                    <NumberInput
                      name="maximum_amount"
                      prefix="$"
                      places={2}
                      requireDecimal
                      leftAlign
                      inputMode="decimal"
                      value={values.maximum_amount ?? ''}
                      onChange={handleFieldChange('maximum_amount')}
                      error={
                        !!(errors.maximum_amount && touched.maximum_amount)
                      }
                    />
                  </Box>
                </Box>
                {errors.maximum_amount && touched.maximum_amount ? (
                  <Box mt={4}>
                    <AlertText
                      variant="error"
                      message={errors.maximum_amount}
                    />
                  </Box>
                ) : (
                  <FormFieldSpacer />
                )}
              </Box>

              <Box row vcenter>
                <Box mr={4}>
                  <Text
                    variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                    color="mono700"
                    i18n="team.employee_profile.tax_deductions.annual_limit"
                  />
                </Box>
                <Box mr={12} pr={44}>
                  <Tooltip
                    content={toI18n(
                      'team.employee_profile.tax_deductions.annual_limit_tooltip_content'
                    )}
                    placement="bottom-start"
                  >
                    <Icon size="small" iconName="Info" color="primary500" />
                  </Tooltip>
                </Box>

                <Box w={172}>
                  <NumberInput
                    name="annual_limit"
                    leftAlign
                    prefix="$"
                    requireDecimal
                    places={2}
                    inputMode="decimal"
                    value={values.annual_limit ?? ''}
                    onChange={handleFieldChange('annual_limit')}
                    error={!!(errors.annual_limit && touched.annual_limit)}
                  />
                </Box>
              </Box>
              {errors.annual_limit && touched.annual_limit ? (
                <Box mt={4}>
                  <AlertText variant="error" message={errors.annual_limit} />
                </Box>
              ) : (
                <FormFieldSpacer />
              )}
            </Box>
          </Box>
          {/* Second row */}
          <Box column={tabletAndBelow} row={!tabletAndBelow} mb={32}>
            <Box grow basis="40%">
              <Box mb={8}>
                <Text
                  variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                  color="mono700"
                  i18n="team.employee_profile.tax_deductions.max_percent_label"
                />
              </Box>
              <Box w={340} mr={36}>
                <NumberInput
                  name="maximum_percent"
                  suffix="%"
                  places={1}
                  requireDecimal
                  inputMode="decimal"
                  leftAlign
                  value={values.maximum_percent ?? ''}
                  onChange={handleFieldChange('maximum_percent')}
                  error={!!(errors.maximum_percent && touched.maximum_percent)}
                />
                {errors.maximum_percent && touched.maximum_percent ? (
                  <Box mt={4}>
                    <AlertText
                      variant="error"
                      message={errors.maximum_percent}
                    />
                  </Box>
                ) : (
                  <FormFieldSpacer />
                )}
              </Box>
            </Box>
            {/* Second column in second row: Priority */}
            <Box grow basis="60%">
              <Box row vcenter mb={8}>
                <Box mr={4}>
                  <Text
                    variant={showFormWithSteps ? 'bodySmBold' : 'bodyBold'}
                    color="mono700"
                    i18n="team.employee_profile.tax_deductions.priority_label"
                  />
                </Box>
                <Box>
                  <Tooltip
                    content={toI18n(
                      'team.employee_profile.tax_deductions.priority_tooltip_content'
                    )}
                    placement="bottom-start"
                  >
                    <Icon size="small" iconName="Info" color="primary500" />
                  </Tooltip>
                </Box>
              </Box>

              <Box w={343}>
                <NumberInput
                  name="priority"
                  places={0}
                  placeholder="1"
                  inputMode="numeric"
                  leftAlign
                  value={values.priority ?? ''}
                  onChange={handleFieldChange('priority', true)}
                  error={!!(errors.priority && touched.priority)}
                />
              </Box>
              <Text
                variant="captions"
                color="mono700"
                i18n="team.employee_profile.tax_deductions.priority_description_label"
              />
              {errors.priority && touched.priority ? (
                <Box mt={4}>
                  <AlertText variant="error" message={errors.priority} />
                </Box>
              ) : (
                <FormFieldSpacer />
              )}
            </Box>
            {/* end of second column in second row */}
          </Box>
          <Box vbottom hright grow basis="30%">
            {children}
          </Box>
          {/* end of second row */}
        </Box>
      )}
    </TabletAndBelow>
  );
};

export default MiscGarnishmentFormFieldRows;
